import settings, { getCountrySettings } from './settings.js';
import { getCurrentCountryCode } from './countryUtils.js';

/**
 * Obtiene la configuración de moneda para un país específico
 * @param {string} countryCode - Código de país (ej: 'ES', 'CO', 'US')
 * @returns {object} Configuración de moneda del país
 */
export const getCurrencyConfigByCountry = (countryCode) => {
    const settingsData = settings();
    const fromTopLevel = settingsData.currency_configurations?.find(
        config => config.countryCode === countryCode
    );
    if (fromTopLevel) return fromTopLevel;

    // settings.js: getCountrySettings(code) -> { country: {...}, ... }
    const node = getCountrySettings(countryCode)?.country;
    if (node) return normalizeCountryNode(countryCode, node);

    // Fallback al país configurado en esta versión (no a España)
    const fallbackCode = getCurrentCountryCode();
    if (fallbackCode && fallbackCode !== countryCode) {
        const fb = getCountrySettings(fallbackCode)?.country;
        if (fb) return normalizeCountryNode(fallbackCode, fb);
    }
    return getDefaultCurrencyConfig();
};

const normalizeCountryNode = (code, node) => ({
    countryCode: code,
    countryName: node.name,
    defaultCurrency: node.currency,
    locale: node.locale,
    currencySymbol: node.currencySymbol,
    decimals: node.decimals ?? 2,
    thousandsSeparator: node.thousandsSeparator ?? (node.locale?.endsWith('-CO') ? '.' : '.'),
    decimalSeparator: node.decimalSeparator ?? ',',
    symbolPosition: node.symbolPosition ?? (code === 'CO' || code === 'MX' ? 'before' : 'after'),
    spaceAfterSymbol: node.spaceAfterSymbol ?? true,
});

/**
 * Obtiene la configuración de moneda basada en el código de moneda
 * @param {string} currencyCode - Código de moneda (ej: 'EUR', 'USD', 'COP')
 * @returns {object} Configuración de moneda
 */
export const getCurrencyConfigByCurrency = (currencyCode) => {
    const settingsData = settings();
    const fromTopLevel = settingsData.currency_configurations?.find(
        config => config.defaultCurrency === currencyCode
    );
    if (fromTopLevel) return fromTopLevel;

    // Buscar entre los países conocidos (CO, ES, MX, ...)
    for (const code of ['CO', 'ES', 'MX']) {
        const node = getCountrySettings(code)?.country;
        if (node?.currency === currencyCode) {
            return normalizeCountryNode(code, node);
        }
    }
    return getDefaultCurrencyConfig();
};

/**
 * Obtiene la configuración de moneda para la empresa actual
 * Prioriza: Company.currencyCode > Company.Country_IdentificationCode > localStorage > fallback
 * @returns {object} Configuración completa de moneda
 */
export const getCompanyCurrencyConfig = () => {
    try {
        // Prioridad 1: País activo de esta versión. countryUtils.getCurrentCountryCode()
        // está locked a CO en esta build; cualquier moneda residual en localStorage /
        // window.seller proveniente de versiones anteriores (EUR) NO debe pisar al
        // país oficial. Si en el futuro la versión deja de estar locked, esta función
        // empezará a depender de las prioridades 2-5.
        const locked = getCurrentCountryCode();
        if (locked) {
            return getCurrencyConfigByCountry(locked);
        }

        // Prioridad 2: Moneda específica de la empresa
        const companyData = JSON.parse(localStorage.getItem('companyData') || '{}');
        if (companyData.currencyCode) {
            return getCurrencyConfigByCurrency(companyData.currencyCode);
        }

        // Prioridad 3: País de la empresa (Country_IdentificationCode)
        if (companyData.Country_IdentificationCode) {
            return getCurrencyConfigByCountry(companyData.Country_IdentificationCode);
        }

        // Prioridad 4: window.seller si está disponible
        if (window.seller?.currencyCode) {
            return getCurrencyConfigByCurrency(window.seller.currencyCode);
        }

        // Prioridad 5: Configuración de store settings
        const storeSettings = JSON.parse(localStorage.getItem('storeSettings') || '[]');
        const currencyConfig = storeSettings.find(setting =>
            setting.tabla === 'CURRENCY' || setting.tabla === 'MONEDA'
        );
        if (currencyConfig?.valor) {
            return getCurrencyConfigByCurrency(currencyConfig.valor);
        }

        return getDefaultCurrencyConfig();
    } catch (error) {
        console.error('Error obteniendo configuración de moneda:', error);
        return getDefaultCurrencyConfig();
    }
};

/**
 * Configuración por defecto: la del país activo de esta versión.
 * Si la versión está locked a CO, devuelve COP; si es ES, devuelve EUR.
 * @returns {object} Configuración por defecto
 */
const getDefaultCurrencyConfig = () => {
    try {
        const code = getCurrentCountryCode();
        const node = getCountrySettings(code)?.country;
        if (node) return normalizeCountryNode(code, node);
    } catch (e) {
        // ignore — caer al hardcoded de abajo
    }
    return {
        countryCode: 'CO',
        countryName: 'Colombia',
        defaultCurrency: 'COP',
        locale: 'es-CO',
        currencySymbol: '$',
        decimals: 0,
        thousandsSeparator: '.',
        decimalSeparator: ',',
        symbolPosition: 'before',
        spaceAfterSymbol: true,
    };
};

/**
 * Formatea un valor monetario según la configuración de la empresa
 * @param {number} value - Valor a formatear
 * @param {string} currencyOverride - Moneda específica a usar (opcional)
 * @param {object} options - Opciones adicionales de formato
 * @returns {string} Valor formateado
 */
export const formatCurrencyAdvanced = (value, currencyOverride = null, options = {}) => {
    if (!value && value !== 0) return "0";
    
    const config = currencyOverride 
        ? getCurrencyConfigByCurrency(currencyOverride)
        : getCompanyCurrencyConfig();
    
    const {
        locale = config.locale,
        currency = config.defaultCurrency,
        minimumFractionDigits = config.decimals,
        maximumFractionDigits = config.decimals,
        useNativeFormat = false
    } = options;

    try {
        if (useNativeFormat) {
            // Usar Intl.NumberFormat nativo del navegador
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits,
                maximumFractionDigits
            }).format(value);
        } else {
            // Usar formato personalizado basado en configuración
            return formatWithCustomConfig(value, config);
        }
    } catch (error) {
        console.error('Error formateando moneda:', error);
        return `${value} ${currency}`;
    }
};

/**
 * Formatea un número según la configuración del país
 * @param {number} value - Valor a formatear
 * @param {object} options - Opciones de formato
 * @returns {string} Número formateado
 */
export const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || isNaN(value)) return "0";
    
    const config = getCompanyCurrencyConfig();
    const {
        locale = config.locale,
        minimumFractionDigits = 2,
        maximumFractionDigits = 2
    } = options;
    
    try {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits,
            maximumFractionDigits
        }).format(value);
    } catch (error) {
        console.error('Error formateando número:', error);
        return value.toString();
    }
};

/**
 * Formatea un porcentaje según la configuración del país
 * @param {number} value - Valor decimal (ej: 0.21 para 21%)
 * @param {object} options - Opciones de formato
 * @returns {string} Porcentaje formateado
 */
export const formatPercentage = (value, options = {}) => {
    if (value === null || value === undefined || isNaN(value)) return "0%";
    
    const config = getCompanyCurrencyConfig();
    const {
        locale = config.locale,
        minimumFractionDigits = 2,
        maximumFractionDigits = 2
    } = options;
    
    try {
        return new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits,
            maximumFractionDigits
        }).format(value);
    } catch (error) {
        console.error('Error formateando porcentaje:', error);
        return `${(value * 100).toFixed(2)}%`;
    }
};

/**
 * Formato personalizado basado en configuración del país
 * @param {number} value - Valor a formatear
 * @param {object} config - Configuración de moneda
 * @returns {string} Valor formateado
 */
const formatWithCustomConfig = (value, config) => {
    const {
        currencySymbol,
        decimals,
        thousandsSeparator,
        decimalSeparator,
        symbolPosition,
        spaceAfterSymbol
    } = config;

    // Convertir a número y aplicar decimales
    const numValue = Number(value);
    const fixedValue = numValue.toFixed(decimals);
    
    // Separar parte entera y decimal
    const [integerPart, decimalPart] = fixedValue.split('.');
    
    // Aplicar separador de miles
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    
    // Construir número completo
    let formattedNumber = formattedInteger;
    if (decimals > 0 && decimalPart) {
        formattedNumber += decimalSeparator + decimalPart;
    }
    
    // Aplicar símbolo de moneda
    const space = spaceAfterSymbol ? ' ' : '';
    if (symbolPosition === 'before') {
        return currencySymbol + space + formattedNumber;
    } else {
        return formattedNumber + space + currencySymbol;
    }
};

/**
 * Obtiene la información de moneda para mostrar en UI
 * @param {string} currencyCode - Código de moneda
 * @returns {object} Información de UI de la moneda
 */
export const getCurrencyDisplayInfo = (currencyCode) => {
    const config = getCurrencyConfigByCurrency(currencyCode);
    
    const iconMap = {
        'EUR': 'mdi-currency-eur',
        'USD': 'mdi-currency-usd',
        'COP': 'mdi-currency-usd',
        'MXN': 'mdi-currency-usd', 
        'GBP': 'mdi-currency-gbp',
        'CAD': 'mdi-currency-usd'
    };
    
    const colorMap = {
        'EUR': 'blue',
        'USD': 'green', 
        'COP': 'orange',
        'MXN': 'red',
        'GBP': 'purple',
        'CAD': 'teal'
    };
    
    return {
        code: currencyCode,
        name: `${config.countryName} (${config.currencySymbol})`,
        symbol: config.currencySymbol,
        icon: iconMap[currencyCode] || 'mdi-currency-usd',
        color: colorMap[currencyCode] || 'grey',
        config: config
    };
};

/**
 * Obtiene lista de monedas disponibles basada en países configurados
 * @returns {array} Lista de monedas disponibles para UI
 */
export const getAvailableCurrencies = () => {
    const settingsData = settings();
    return settingsData.currency_configurations?.map(config => ({
        code: config.defaultCurrency,
        name: `${config.defaultCurrency} - ${config.countryName} (${config.currencySymbol})`,
        description: `Moneda oficial de ${config.countryName}`,
        symbol: config.currencySymbol,
        countryCode: config.countryCode,
        locale: config.locale
    })) || [];
};

export default {
    getCurrencyConfigByCountry,
    getCurrencyConfigByCurrency, 
    getCompanyCurrencyConfig,
    formatCurrencyAdvanced,
    formatNumber,
    formatPercentage,
    getCurrencyDisplayInfo,
    getAvailableCurrencies
};
